import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"

import { Button } from "@/components/common/Button"
import { ConfirmationDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { FilesTable } from "@/components/Tables/Aeros/components/FilesTable"
import { useFileColumns } from "@/components/Tables/Aeros/hooks/useFileColumns"
import { useGetFileUploadData } from "@/components/upload/hooks/useGetFileUploadData"
import type { SalesOrder, SignedUrl } from "@/graphql/codegen/graphql"
import { ReferenceTableName } from "@/graphql/codegen/graphql"
import { useFilesRemove } from "@/hooks/useFilesRemove"
import { NotificationType } from "@/schemas/notification"
import { SALES_ORDER_FILE_FIELD_GROUPS, SALES_ORDER_FILE_TYPE_MAPPING } from "@/screens/CustomerOrders/constants"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface SalesOrderDocumentsDialogProps {
  onClose: () => void
  salesOrder: SalesOrder
}

export const SalesOrderDocumentsDialog: FC<SalesOrderDocumentsDialogProps> = ({ onClose, salesOrder }) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [deletingDocument, setDeletingDocument] = useState<SignedUrl>()

  const { fileList, fileFetching, refetchFileUploadData } = useGetFileUploadData({
    referenceId: salesOrder.salesOrderId,
    fieldGroups: SALES_ORDER_FILE_FIELD_GROUPS,
    referenceTableName: ReferenceTableName.SalesOrder,
  })
  const { removeSingleFile: handleFileRemove } = useFilesRemove(refetchFileUploadData)

  const handleDeleteConfirm = async () => {
    const fileId = deletingDocument?.file?.fileId
    setDeletingDocument(undefined)
    if (!fileId) return

    const isDeleted = await handleFileRemove(fileId)
    notify(
      isDeleted
        ? { type: NotificationType.Success, message: "Document deleted" }
        : { type: NotificationType.Error, message: "Failed to delete document" }
    )
  }

  const columns = useFileColumns({
    typeToChip: SALES_ORDER_FILE_TYPE_MAPPING,
    onDelete: setDeletingDocument,
  })

  return deletingDocument ? (
    <ConfirmationDialog
      title='Delete Document'
      content='Are you sure you want to delete this document? This action cannot be undone.'
      closeLabel='Cancel'
      confirmLabel='Delete'
      isWarning
      onClose={() => setDeletingDocument(undefined)}
      onConfirm={handleDeleteConfirm}
    />
  ) : (
    <Dialog open onClose={onClose} fullWidth maxWidth='lg' data-testid='sales-order.documents-dialog'>
      <DialogHeader title='View Documents' onClose={onClose} />
      <DialogContent className='p-10'>
        <FilesTable
          columns={columns}
          data={fileList}
          isLoading={fileFetching}
          noDataMessage='No sales order documents found'
        />
      </DialogContent>

      <DialogActions className='justify-end px-10 pb-10'>
        <Button appearance='contained' onClick={onClose} data-testid='sales-order.documents-dialog.close-button'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
